import React, { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Switch, Typography } from "@mui/material";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";

const discountTypes = [
  { value: "global", label: "Global" },
  { value: "volume", label: "Volume" },
  { value: "sibling", label: "Sibling" },
  { value: "bundle", label: "Bundle" },
];

const defaultConfig = {
  global: { amount: "", is_percentage: true, threshold: "", target: "product" },
  volume: { target: "group_class", thresholds: [{ min: "", discount: "" }], is_percentage: true },
  sibling: { nth_child: "", discount: "", is_percentage: true },
  bundle: { target: "private_lesson", period: "quarterly", discount: "", is_percentage: true },
};

function DiscountForm({ open, onClose, onSuccess, access_token, initialValues }) {
  const isEdit = Boolean(initialValues);
  const [type, setType] = useState(initialValues?.type || "global");
  const [name, setName] = useState(initialValues?.name || "");
  const [isActive, setIsActive] = useState(initialValues?.is_active ?? true);
  const [config, setConfig] = useState(initialValues?.config || defaultConfig[type]);

  useEffect(() => {
    if (initialValues) {
      setType(initialValues.type);
      setName(initialValues.name);
      setIsActive(initialValues.is_active);
      if (initialValues.type === 'global') {
        setConfig({ ...defaultConfig['global'], ...initialValues.config, target: 'product' });
      } else {
        setConfig(initialValues.config);
      }
    } else {
      setType("global");
      setName("");
      setIsActive(true);
      setConfig(defaultConfig["global"]);
    }
  }, [initialValues, open]);

  useEffect(() => {
    setConfig((prev) => {
      // Si on édite et que le type n'a pas changé, on garde la config existante
      if (isEdit && initialValues && initialValues.type === type) {
        return initialValues.config;
      }
      // Sinon, on met la valeur par défaut
      if (type === 'global') {
        return { ...defaultConfig['global'], target: 'product' };
      }
      return defaultConfig[type];
    });
    // eslint-disable-next-line
  }, [type]);

  const handleConfigChange = (field, value) => {
    setConfig((prev) => {
      if (type === 'global') {
        return { ...prev, [field]: value, target: 'product' };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleThresholdChange = (idx, field, value) => {
    setConfig((prev) => ({
      ...prev,
      thresholds: prev.thresholds.map((t, i) => (i === idx ? { ...t, [field]: value } : t)),
    }));
  };

  const addThreshold = () => {
    setConfig((prev) => ({ ...prev, thresholds: [...(prev.thresholds || []), { min: "", discount: "" }] }));
  };

  const removeThreshold = (idx) => {
    setConfig((prev) => ({ ...prev, thresholds: prev.thresholds.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name,
      type,
      is_active: isActive,
      config,
    };
    const apiCall = isEdit
      ? Api.put(Urls.UPDATE_DISCOUNT + initialValues.id, payload, { headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` } })
      : Api.post(Urls.CREATE_DISCOUNT, payload, { headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` } });
    apiCall
      .then((res) => {
        toast.success(isEdit ? "Discount updated" : "Discount created");
        const discount = res.data.data ?? res.data;
        if (discount && discount.id) {
          onSuccess(discount);
        }
        onClose();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to save discount");
      });
  };

  // Options de cible communes aux types volume et bundle
  const targetOptions = [
    { value: 'group_class', label: 'Group class' },
    { value: 'court_booking', label: 'Court booking' },
    { value: 'private_lesson', label: 'Private lesson' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Discount" : "Add Discount"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField label="Name" value={name} onChange={e => setName(e.target.value)} required fullWidth />
            <TextField select label="Type" value={type} onChange={e => setType(e.target.value)} fullWidth>
              {discountTypes.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>Active</Typography>
              <Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />
            </Box>
            {/* Champs dynamiques selon le type */}
            {type === "global" && (
              <>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField label="Amount" type="number" value={config.amount} onChange={e => handleConfigChange("amount", e.target.value)} required />
                  <Typography>Is %</Typography>
                  <Switch checked={config.is_percentage} onChange={e => handleConfigChange("is_percentage", e.target.checked)} />
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField label="Threshold" type="number" value={config.threshold} onChange={e => handleConfigChange("threshold", e.target.value)} required />
                  <TextField label="Target" value={config.target} InputProps={{ readOnly: true }} />
                </Box>
              </>
            )}
            {type === "volume" && (
              <>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField select label="Target" value={config.target} onChange={e => handleConfigChange("target", e.target.value)} required>
                    {targetOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <Typography>Is %</Typography>
                  <Switch checked={config.is_percentage} onChange={e => handleConfigChange("is_percentage", e.target.checked)} />
                </Box>
                <Box>
                  <Typography fontWeight="bold">Thresholds</Typography>
                  {config.thresholds?.map((t, idx) => (
                    <Box key={idx} display="flex" gap={1} alignItems="center" mb={1}>
                      <TextField label="Min" type="number" value={t.min} onChange={e => handleThresholdChange(idx, "min", e.target.value)} required sx={{ width: 80 }} />
                      <TextField label="Discount" type="number" value={t.discount} onChange={e => handleThresholdChange(idx, "discount", e.target.value)} required sx={{ width: 100 }} />
                      <Button onClick={() => removeThreshold(idx)} color="error">X</Button>
                    </Box>
                  ))}
                  <Button onClick={addThreshold} size="small">Add Threshold</Button>
                </Box>
              </>
            )}
            {type === "sibling" && (
              <Box display="flex" gap={2} alignItems="center">
                <TextField label="Nth Child" type="number" value={config.nth_child} onChange={e => handleConfigChange("nth_child", e.target.value)} required />
                <TextField label="Discount" type="number" value={config.discount} onChange={e => handleConfigChange("discount", e.target.value)} required />
                <Typography>Is %</Typography>
                <Switch checked={config.is_percentage} onChange={e => handleConfigChange("is_percentage", e.target.checked)} />
              </Box>
            )}
            {type === "bundle" && (
              <Box display="flex" gap={2} alignItems="center">
                <TextField select label="Target" value={config.target} onChange={e => handleConfigChange("target", e.target.value)} required>
                  {targetOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Period" value={config.period} onChange={e => handleConfigChange("period", e.target.value)} required />
                <TextField label="Discount" type="number" value={config.discount} onChange={e => handleConfigChange("discount", e.target.value)} required />
                <Typography>Is %</Typography>
                <Switch checked={config.is_percentage} onChange={e => handleConfigChange("is_percentage", e.target.checked)} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">{isEdit ? "Save" : "Create"}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default DiscountForm; 